import React, { useEffect, useMemo, useState } from "react";
import { FaStore, FaCalendarCheck, FaUserCheck } from "react-icons/fa";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import "./PublicDemoBookingPage.css";
import "../pages/auth/LoginStyles.css";
import "../pages/auth/PremiumAuth.css";

const CRM_API_BASE =
  "https://softiss-crm-backend.bluecoast-efc2a516.eastus.azurecontainerapps.io/api";
const DEMO_DURATION_MINUTES = 30;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_error) {
    return "UTC";
  }
}

function getTimeZoneOptions() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch (_error) {
    // Fallback list below.
  }

  return [
    "UTC",
    "America/Chicago",
    "America/New_York",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Paris",
    "Asia/Dubai",
    "Asia/Lagos",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];
}

function getUtmParams() {
  const q = new URLSearchParams(window.location.search);
  return {
    utmSource: q.get("utm_source") || "",
    utmMedium: q.get("utm_medium") || "",
    utmCampaign: q.get("utm_campaign") || "",
  };
}

function formatInTimeZone(iso, timeZone, options) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      ...options,
    }).format(new Date(iso));
  } catch (_error) {
    return new Date(iso).toLocaleString();
  }
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getMondayStart(date) {
  const start = startOfDay(date);
  const day = start.getDay();
  const offsetToMonday = day === 0 ? -6 : 1 - day;
  return addDays(start, offsetToMonday);
}

function getWeekRange(weekOffset) {
  const start = addDays(getMondayStart(new Date()), weekOffset * 7);
  const end = addDays(start, 7);

  return {
    start,
    end,
    fromIso: start.toISOString(),
    toIso: end.toISOString(),
  };
}

function mapBookingError(status) {
  if (status === 400) return "Please complete all required fields correctly.";
  if (status === 404) {
    return "Selected presenter unavailable. Please choose another time.";
  }
  if (status === 409) return "That slot was just taken. Please pick another slot.";
  return "Something went wrong. Please try again in a moment.";
}

export default function PublicDemoBookingPage() {
  const [form, setForm] = useState({
    attendeeName: "",
    attendeeEmail: "",
    phoneNumber: "",
    businessName: "",
    website: "",
    notes: "",
  });
  const [weekOffset, setWeekOffset] = useState(0);
  const [displayTimeZone, setDisplayTimeZone] = useState(getBrowserTimeZone);
  const [timeZoneOptions] = useState(getTimeZoneOptions);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDayKey, setSelectedDayKey] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMeeting, setSuccessMeeting] = useState(null);

  useEffect(() => {
    if (!successMeeting) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      window.location.assign("https://www.ogestyle.com");
    }, 5000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [successMeeting]);

  const weekRange = useMemo(() => getWeekRange(weekOffset), [weekOffset]);

  useEffect(() => {
    let active = true;

    async function loadAvailability() {
      setLoadingSlots(true);
      setAvailabilityError("");

      try {
        const params = new URLSearchParams({
          from: weekRange.fromIso,
          to: weekRange.toIso,
          slotMinutes: String(DEMO_DURATION_MINUTES),
        });

        const res = await fetch(
          `${CRM_API_BASE}/demo-scheduling/public/availability?${params.toString()}`,
          { method: "GET" },
        );
        const body = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(body?.message || "Could not load availability");
        }

        if (!active) return;

        const nextSlots = Array.isArray(body.slots) ? body.slots : [];
        setSlots(nextSlots);

        if (
          selectedSlot &&
          !nextSlots.some(
            (slot) =>
              slot.startIso === selectedSlot.startIso &&
              slot.presenterId === selectedSlot.presenterId,
          )
        ) {
          setSelectedSlot(null);
        }
      } catch (error) {
        if (!active) return;
        setSlots([]);
        setAvailabilityError(
          error?.message || "Unable to load demo slots right now.",
        );
      } finally {
        if (active) setLoadingSlots(false);
      }
    }

    loadAvailability();

    return () => {
      active = false;
    };
  }, [weekRange.fromIso, weekRange.toIso]);

  const dayCards = useMemo(() => {
    const slotMap = slots.reduce((acc, slot) => {
      const key = formatInTimeZone(slot.startIso, displayTimeZone, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(slot);
      return acc;
    }, {});

    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(weekRange.start, index);
      const key = formatInTimeZone(date.toISOString(), displayTimeZone, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const daySlots = slotMap[key] || [];
      return {
        key,
        slotCount: daySlots.length,
        slots: daySlots,
        weekdayShort: formatInTimeZone(date.toISOString(), displayTimeZone, {
          weekday: "short",
        }),
        dayOfMonth: formatInTimeZone(date.toISOString(), displayTimeZone, {
          day: "numeric",
        }),
      };
    });
  }, [displayTimeZone, slots, weekRange.start]);

  useEffect(() => {
    if (!dayCards.length) {
      return;
    }

    if (selectedDayKey && dayCards.some((day) => day.key === selectedDayKey)) {
      return;
    }

    const firstDayWithSlots = dayCards.find((day) => day.slotCount > 0);
    setSelectedDayKey(firstDayWithSlots?.key || dayCards[0].key);
  }, [dayCards, selectedDayKey]);

  const selectedDay =
    dayCards.find((day) => day.key === selectedDayKey) || dayCards[0] || null;
  const visibleSlots = selectedDay?.slots || [];

  const weekLabel = `${formatInTimeZone(weekRange.start.toISOString(), displayTimeZone, {
    month: "short",
    day: "numeric",
  })} - ${formatInTimeZone(addDays(weekRange.start, 6).toISOString(), displayTimeZone, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
  }

  function validateForm() {
    const nextErrors = {};

    if (!String(form.attendeeName || "").trim()) {
      nextErrors.attendeeName = "Your name is required.";
    }

    const emailValue = String(form.attendeeEmail || "").trim();
    if (!emailValue) {
      nextErrors.attendeeEmail = "Email is required.";
    } else if (!EMAIL_REGEX.test(emailValue)) {
      nextErrors.attendeeEmail = "Enter a valid email address.";
    }

    const phoneDigits = String(form.phoneNumber || "").replace(/\D/g, "");
    if (!phoneDigits) {
      nextErrors.phoneNumber = "Phone number is required.";
    } else if (phoneDigits.length < 10) {
      nextErrors.phoneNumber = "Enter a valid phone number.";
    }

    if (!String(form.businessName || "").trim()) {
      nextErrors.businessName = "Business name is required.";
    }

    if (!selectedSlot) {
      nextErrors.slot = "Please select a time slot.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    setBooking(true);

    try {
      const utm = getUtmParams();
      const payload = {
        attendeeName: String(form.attendeeName || "").trim(),
        attendeeEmail: String(form.attendeeEmail || "").trim(),
        phoneNumber: String(form.phoneNumber || "").trim(),
        businessName: String(form.businessName || "").trim(),
        website: String(form.website || "").trim(),
        notes: String(form.notes || "").trim(),
        startIso: selectedSlot.startIso,
        presenterId: selectedSlot.presenterId,
        durationMinutes: DEMO_DURATION_MINUTES,
        utmSource: utm.utmSource,
        utmMedium: utm.utmMedium,
        utmCampaign: utm.utmCampaign,
        landingPath: window.location.pathname,
      };

      const res = await fetch(`${CRM_API_BASE}/demo-scheduling/public/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 409) {
          setSelectedSlot(null);
        }
        throw { status: res.status, body };
      }

      setSuccessMeeting(body?.meeting || null);
    } catch (error) {
      setSubmitError(mapBookingError(error?.status));
    } finally {
      setBooking(false);
    }
  }

  if (successMeeting) {
    return (
      <main className="public-demo-page">
        <section className="public-demo-card public-demo-success-card">
          <h1>Demo booked successfully</h1>
          <p>
            Your demo is confirmed. We have sent the meeting details to your
            email.
          </p>
          <p>You will be redirected to www.ogestyle.com in 5 seconds.</p>
          <div className="public-demo-meeting-meta">
            <p>
              <strong>Starts:</strong>{" "}
              {formatInTimeZone(successMeeting.startIso, displayTimeZone, {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
            {successMeeting.guestLink && (
              <p>
                <strong>Meeting link:</strong>{" "}
                <a
                  href={successMeeting.guestLink}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Join meeting
                </a>
              </p>
            )}
          </div>
        </section>
      </main>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="login-page og-premium-auth og-demo-schedule">
        {/* Left branding panel — same premium concept as registration/login */}
        <div className="login-branding">
          <div className="og-branding">
            <p className="og-eyebrow">Book a live walkthrough</p>
            <h2 className="og-brand-title">See OGESTYLE in action</h2>
            <p className="og-brand-sub">
              A free, guided 30-minute walkthrough with an OGESTYLE specialist.
              See the real product, ask anything, and find out how it fits the
              way you work.
            </p>
            <div className="og-features">
              <div className="og-feature">
                <span className="og-feature-ic"><FaStore /></span>
                <span>Built for beauty businesses</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaCalendarCheck /></span>
                <span>See the real product, live</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaUserCheck /></span>
                <span>Ask anything — setup &amp; migration</span>
              </div>
            </div>
            <div className="og-trust">
              <span>Free &amp; no obligation</span>
              <span>30 minutes</span>
              <span>Tailored to your business</span>
            </div>
          </div>
        </div>

        {/* Scheduler fills the whole right panel (single colour, compact) */}
        <div className="login-form-panel">
          <div className="login-container">
            <section className="public-demo-card public-demo-card--inner">
              <header className="login-header">
                <h2>Pick your demo time</h2>
                <p className="subtitle">
                  Choose a slot below and we&rsquo;ll email you the meeting
                  invite.
                </p>
              </header>

        <div className="public-demo-meta-row">
          <div className="public-demo-timezone-group">
            <label htmlFor="public-demo-timezone" className="public-demo-timezone-label">
              Times shown in
            </label>
            <select
              id="public-demo-timezone"
              className="public-demo-timezone-select"
              value={displayTimeZone}
              onChange={(event) => setDisplayTimeZone(event.target.value)}
              disabled={loadingSlots || booking}
            >
              {timeZoneOptions.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>
          <p className="public-demo-duration-copy">
            Duration: 30 minutes
          </p>
        </div>

        <section className="public-demo-scheduler" aria-live="polite">
          <div className="public-demo-week-bar">
            <button
              type="button"
              className="public-demo-week-arrow"
              onClick={() => setWeekOffset((prev) => Math.max(prev - 1, 0))}
              disabled={booking || loadingSlots || weekOffset === 0}
              aria-label="Previous week"
            >
              &lt;
            </button>
            <h2>{weekLabel}</h2>
            <button
              type="button"
              className="public-demo-week-arrow"
              onClick={() => setWeekOffset((prev) => prev + 1)}
              disabled={booking || loadingSlots}
              aria-label="Next week"
            >
              &gt;
            </button>
          </div>

          <div className="public-demo-day-cards">
            {dayCards.map((day) => {
              const isSelected = day.key === selectedDayKey;
              return (
                <button
                  key={day.key}
                  type="button"
                  className={`public-demo-day-card ${isSelected ? "selected" : ""}`}
                  onClick={() => setSelectedDayKey(day.key)}
                  disabled={booking || loadingSlots}
                  aria-label={`${day.weekdayShort} ${day.dayOfMonth}, ${
                    day.slotCount > 0 ? `${day.slotCount} open slots` : "no open slots"
                  }`}
                >
                  <span className="public-demo-day-name">{day.weekdayShort}</span>
                  <span className="public-demo-day-number">{day.dayOfMonth}</span>
                  <span
                    className={`public-demo-day-dot ${day.slotCount > 0 ? "has-open" : ""}`}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>

          {loadingSlots && <p className="public-demo-status">Loading available slots...</p>}
          {!loadingSlots && availabilityError && (
            <p className="public-demo-error public-demo-status">{availabilityError}</p>
          )}
          {!loadingSlots && !availabilityError && visibleSlots.length === 0 && (
            <p className="public-demo-status">No time slots available for the selected day.</p>
          )}

          {!loadingSlots && !availabilityError && visibleSlots.length > 0 && (
            <div className="public-demo-slot-list">
              {visibleSlots.map((slot) => {
                const isSelected =
                  selectedSlot?.startIso === slot.startIso &&
                  selectedSlot?.presenterId === slot.presenterId;

                return (
                  <button
                    type="button"
                    key={`${slot.startIso}-${slot.presenterId}`}
                    className={`public-demo-slot ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedSlot(slot);
                      setFieldErrors((prev) => ({ ...prev, slot: "" }));
                    }}
                    disabled={booking}
                  >
                    {formatInTimeZone(slot.startIso, displayTimeZone, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </button>
                );
              })}
            </div>
          )}

          {fieldErrors.slot && (
            <p className="public-demo-error public-demo-slot-error">{fieldErrors.slot}</p>
          )}
        </section>

        <form onSubmit={handleSubmit} noValidate className="public-demo-form">
          <div className="public-demo-grid">
            <div className="public-demo-input-wrap">
              <input
                id="demo-attendee-name"
                type="text"
                placeholder=" "
                value={form.attendeeName}
                onChange={(e) => updateField("attendeeName", e.target.value)}
                disabled={booking}
              />
              <label className="public-demo-input-label" htmlFor="demo-attendee-name">
                Your Name
              </label>
              {fieldErrors.attendeeName && (
                <span className="public-demo-error">{fieldErrors.attendeeName}</span>
              )}
            </div>

            <div className="public-demo-input-wrap">
              <input
                id="demo-business-name"
                type="text"
                placeholder=" "
                value={form.businessName}
                onChange={(e) => updateField("businessName", e.target.value)}
                disabled={booking}
              />
              <label className="public-demo-input-label" htmlFor="demo-business-name">
                Business Name
              </label>
              {fieldErrors.businessName && (
                <span className="public-demo-error">{fieldErrors.businessName}</span>
              )}
            </div>

            <div className="public-demo-input-wrap">
              <input
                id="demo-attendee-email"
                type="email"
                placeholder=" "
                value={form.attendeeEmail}
                onChange={(e) => updateField("attendeeEmail", e.target.value)}
                disabled={booking}
              />
              <label className="public-demo-input-label" htmlFor="demo-attendee-email">
                Email
              </label>
              {fieldErrors.attendeeEmail && (
                <span className="public-demo-error">{fieldErrors.attendeeEmail}</span>
              )}
            </div>

            <div className="public-demo-input-wrap">
              <input
                id="demo-phone-number"
                type="tel"
                placeholder=" "
                value={form.phoneNumber}
                onChange={(e) => updateField("phoneNumber", e.target.value)}
                disabled={booking}
              />
              <label className="public-demo-input-label" htmlFor="demo-phone-number">
                Phone Number
              </label>
              {fieldErrors.phoneNumber && (
                <span className="public-demo-error">{fieldErrors.phoneNumber}</span>
              )}
            </div>
          </div>

          <div className="public-demo-field-full public-demo-input-wrap">
            <input
              id="demo-website"
              type="url"
              placeholder=" "
              value={form.website}
              onChange={(e) => updateField("website", e.target.value)}
              disabled={booking}
            />
            <label className="public-demo-input-label" htmlFor="demo-website">
              Website (optional)
            </label>
          </div>

          <div className="public-demo-field-full public-demo-input-wrap public-demo-textarea-wrap">
            <textarea
              id="demo-notes"
              rows={4}
              placeholder=" "
              value={form.notes}
              onChange={(e) => updateField("notes", e.target.value)}
              disabled={booking}
            />
            <label className="public-demo-input-label" htmlFor="demo-notes">
              Notes (optional)
            </label>
          </div>

          {submitError && <p className="public-demo-error">{submitError}</p>}

          <div className="public-demo-actions">
            <button
              type="submit"
              className="public-demo-submit"
              disabled={booking || loadingSlots || !selectedSlot}
            >
              {booking ? "Scheduling..." : "Schedule Demo"}
            </button>
          </div>
        </form>
            </section>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
}
